import { taskListColorSchema } from "@orsane/schema/task-lists";
import { z } from "zod";
import { userOutputSchema } from "./users";

// Priority
export const priorityValues = ["low", "medium", "high", "urgent"] as const;
export const priorityEnumSchema = z.enum(priorityValues);
export type Priority = z.infer<typeof priorityEnumSchema>;

// Custom attribute type
export const customAttributeTypeValues = [
    "text",
    "number",
    "select_one",
    "select_multiple",
    "timer",
    "orbit_support_project",
] as const;
export type CustomAttributeType = z.infer<typeof customAttributeTypeEnumSchema>;
export const customAttributeTypeEnumSchema = z.enum(customAttributeTypeValues);

export const taskStatusOutputSchema = z.object({
    id: z.number(),
    name: z.string(),
    order: z.number(),
    color: z.string().nullable(),
    isCompleted: z.boolean(),
    isDefault: z.boolean(),
    isSystem: z.boolean(),
});
export type TaskStatusOutput = z.infer<typeof taskStatusOutputSchema>;

export const shortTaskOutputSchema = z.object({
    id: z.number(),
    key: z.string(),
    number: z.number(),
    title: z.string(),
});

export const taskOutputSchema = z.object({
    id: z.number(),
    key: z.string(),
    number: z.number(),
    title: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    taskStatusId: z.number(),
    dueDate: z.string().nullable(),
    hasDescription: z.boolean(),
    hasActivity: z.boolean(),
    taskStatus: taskStatusOutputSchema.nullable(),
    taskLists: z.array(
        z.object({
            id: z.number(),
            name: z.string(),
            color: taskListColorSchema,
        })
    ),
    assignees: z.array(userOutputSchema),
    createdBy: userOutputSchema,
    priority: priorityEnumSchema.nullable(),
    parentTask: shortTaskOutputSchema.nullable(),
    customAttributeValues: z.array(
        z.object({
            id: z.number(),
            value: z.any(),
        })
    ),
    isFilterMatch: z.boolean(),
});
export type TaskOutput = z.infer<typeof taskOutputSchema>;
export type ShortTaskOutput = z.infer<typeof shortTaskOutputSchema>;
export type TaskOutputWithNewlyCreated = TaskOutput & {
    isNewlyCreated: boolean;
    justCreated: boolean | undefined;
    [key: string]: any; // Index signature to allow dynamic property assignment (used for grouping)
};

export const taskUpdateOutputSchema = z.object({
    id: z.number(),
    attributeId: z.string(),
    attributeName: z.string(),
    attributeType: z.enum(customAttributeTypeValues).nullable(),
    attributeValue: z.string().nullable(),
    taskId: z.number(),
    createdAt: z.date(),
    user: userOutputSchema,
});
export type TaskUpdateOutput = z.infer<typeof taskUpdateOutputSchema>;

export const customAttributeListOutputSchema = z.object({
    id: z.number(),
    name: z.string(),
    type: z.enum(customAttributeTypeValues),
});

export enum BuiltInAttribute {
    parentTask = "parentTask",
    taskStatus = "taskStatus",
    id = "id",
    dueDate = "dueDate",
    priority = "priority",
    assignee = "assignee",
    lists = "lists",
    createdAt = "createdAt",
    title = "title",
    createdBy = "createdBy",
    manual = "manual",
}

export type CustomAttributeListOutput = z.infer<typeof customAttributeListOutputSchema>;
